import { buildParamsForChart, getChartDynamicFilters } from './dashboardData';
import { MONTH_YEAR_PRESET_PARAMS } from './monthYearPreset';

/** Pares convencionales desde/hasta (prioridad: día → mes → año → tiempo). */
export const PERIOD_RANGE_PARAM_PAIRS = [
  [MONTH_YEAR_PRESET_PARAMS.dayDesde, MONTH_YEAR_PRESET_PARAMS.dayHasta],
  [MONTH_YEAR_PRESET_PARAMS.monthDesde, MONTH_YEAR_PRESET_PARAMS.monthHasta],
  [MONTH_YEAR_PRESET_PARAMS.yearDesde, MONTH_YEAR_PRESET_PARAMS.yearHasta],
  ['timeDesde', 'timeHasta'],
];

/** Fechas ISO en metadato de período: DD-MM-YYYY (misma convención que chips de filtro). */
export function formatPeriodDisplayDate(value) {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, yyyy, mm, dd] = dateOnly;
    return `${dd}-${mm}-${yyyy}`;
  }
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z)?$/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd}-${mm}-${yyyy}`;
  }
  return str;
}

function chartHasEnabledParam(chart, paramName) {
  const p = String(paramName || '').trim();
  return getChartDynamicFilters(chart).some(
    (f) => f?.enabled && String(f.paramName || '').trim() === p
  );
}

function pickDateRangePair(chart) {
  for (const [desde, hasta] of PERIOD_RANGE_PARAM_PAIRS) {
    if (chartHasEnabledParam(chart, desde) && chartHasEnabledParam(chart, hasta)) {
      return { desdeParam: desde, hastaParam: hasta };
    }
  }
  return null;
}

/** KPI en presentación/reporte: titulo + periodo + valor + YoY no caben en h=2. */
export function kpiCompactExportNeedsTallTile(chart) {
  if (chart?.type !== 'kpi') return false;
  const viz = chart.visualizationSpec?.visualization;
  if (viz?.kpiComparePriorYear) return true;
  const filters = getChartDynamicFilters(chart).filter((f) => f?.enabled && f?.paramName);
  const names = new Set(filters.map((f) => String(f.paramName).trim()));
  if (PERIOD_RANGE_PARAM_PAIRS.some(([desde, hasta]) => names.has(desde) && names.has(hasta))) {
    return true;
  }
  return filters.some((f) => f.filterType === 'date');
}

/**
 * Texto de período para tile en presentación / reporte, o null si no aporta info
 * (sin par de fechas resuelto para este chart).
 */
export function getChartPeriodCaption(chart, options = {}) {
  if (!chart?.queryId) return null;
  const pair = pickDateRangePair(chart);
  if (!pair) return null;

  const {
    globalFilterValues = {},
    globalFilterParamNames = [],
    globalFilterParamAliases = {},
    getDynamicFilterValue,
  } = options;

  const params = buildParamsForChart(chart, {
    globalFilterValues,
    globalFilterParamNames,
    globalFilterParamAliases,
    getDynamicFilterValue,
  });

  const { desdeParam, hastaParam } = pair;
  const desdeRaw = params[desdeParam];
  const hastaRaw = params[hastaParam];
  if (
    desdeRaw == null ||
    hastaRaw == null ||
    String(desdeRaw).trim() === '' ||
    String(hastaRaw).trim() === ''
  ) {
    return null;
  }

  // El tile siempre muestra a que periodo responde, aunque el rango venga de un filtro
  // global y ya se vea arriba: en presentacion cada KPI debe ser legible por si solo
  // (ej. captura suelta de un tile, export individual), sin depender de la barra superior.
  const desde = formatPeriodDisplayDate(desdeRaw);
  const hasta = formatPeriodDisplayDate(hastaRaw);
  if (!desde || !hasta) return null;

  const text = `${desde} al ${hasta}`;
  return { text, title: text };
}
